import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Palmtree, CircleAlert, TriangleAlert, Check } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, plural } from "../lib/formato.js";
import { usePode } from "../shell/Sessao.js";

const ESTADO: Record<string, string> = {
  solicitada: "Solicitada", aprovada_gestor: "Aprovada pelo gestor",
  aprovada_rh: "Aprovada pelo RH", avisada: "Aviso entregue",
  paga: "Paga", gozada: "Gozada", cancelada: "Cancelada",
};

/** O próximo passo de cada estado. Fluxo linear, sem atalho. */
const PROXIMO: Record<string, { estado: string; rotulo: string }> = {
  solicitada: { estado: "aprovada_gestor", rotulo: "Aprovar como gestor" },
  aprovada_gestor: { estado: "aprovada_rh", rotulo: "Aprovar como RH" },
  aprovada_rh: { estado: "avisada", rotulo: "Registrar o aviso" },
  avisada: { estado: "paga", rotulo: "Registrar o pagamento" },
  paga: { estado: "gozada", rotulo: "Marcar como gozada" },
};

export function Ferias() {
  const [programando, setProgramando] = useState(false);
  const podeProgramar = usePode("ferias.programar");
  const podeAprovar = usePode("ferias.aprovar");
  const qc = useQueryClient();

  const periodos = useQuery({
    queryKey: ["ferias", "periodos"],
    queryFn: () => trpc.fechamento.periodosAquisitivos.query({ soAbertos: true }),
  });
  const programacoes = useQuery({
    queryKey: ["ferias", "programacoes"],
    queryFn: () => trpc.fechamento.programacoes.query({}),
  });

  const avancar = useMutation({
    mutationFn: (v: { id: string; estado: string }) =>
      trpc.fechamento.avancarFerias.mutate({ id: v.id, estado: v.estado as "aprovada_rh" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ferias"] }),
  });

  // Passou de 12 meses do fim do aquisitivo sem gozo: a empresa paga em DOBRO
  // (art. 137). Negativo já É dobra, não é "atrasado".
  const emDobra = (periodos.data ?? []).filter((p) => p.diasAteVencer < 0);
  const vencendo = (periodos.data ?? []).filter(
    (p) => p.diasAteVencer >= 0 && p.diasAteVencer <= 90);

  return (
    <Pagina
      secao="Folha" titulo="Férias"
      descricao="Período aquisitivo, concessivo e o risco de dobra. O aviso sai 30 dias antes e o pagamento até 2 dias antes do início."
      acoes={podeProgramar && (periodos.data?.length ?? 0) > 0 ? (
        <button className="botao" data-variante="primario" onClick={() => setProgramando(true)}>
          <Palmtree aria-hidden size={15} /> Programar férias
        </button>
      ) : undefined}
    >
      {periodos.isPending && <Carregando linhas={5} />}
      {periodos.isError && <Falha erro={periodos.error} tentarDeNovo={() => void periodos.refetch()} />}

      {emDobra.length > 0 && (
        <Cartao titulo="Já em dobra"
                subtitulo="CLT art. 137: passou do período concessivo, a empresa paga em dobro">
          <ul className="eop-ferias">
            {emDobra.map((p) => (
              <li key={p.id} data-critico>
                <TriangleAlert aria-hidden size={15} className="eop-ferias-icone" />
                <span className="min-w-0 flex-1">
                  <span className="eop-ferias-nome">{p.nome}</span>
                  <span className="eop-ferias-sub">
                    aquisitivo de {fData(p.inicio)} a {fData(p.fim)} · venceu em{" "}
                    {fData(p.concessivoAte)}, há {plural(Math.abs(p.diasAteVencer), "dia", "dias")}
                  </span>
                </span>
                <span className="num eop-ferias-saldo">{p.diasSaldo}d</span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {vencendo.length > 0 && (
        <Cartao titulo="Vence em até 90 dias"
                subtitulo="Programar agora ainda evita a dobra">
          <ul className="eop-ferias">
            {vencendo.map((p) => (
              <li key={p.id} data-atencao={p.diasAteVencer <= 30 || undefined}>
                <Palmtree aria-hidden size={15} className="eop-ferias-icone" />
                <span className="min-w-0 flex-1">
                  <span className="eop-ferias-nome">{p.nome}</span>
                  <span className="eop-ferias-sub">
                    vence em {fData(p.concessivoAte)} · {plural(p.diasAteVencer, "dia", "dias")}
                    {p.faltas > 0 && ` · ${plural(p.faltas, "falta", "faltas")}, direito de ${p.diasDireito}d`}
                  </span>
                </span>
                <span className="num eop-ferias-saldo">{p.diasSaldo}d</span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {periodos.isSuccess && periodos.data.length === 0 && (
        <EmptyState
          titulo="Nenhum período aquisitivo aberto"
          descricao="O período aquisitivo abre na admissão e fecha 12 meses depois. Faltas injustificadas reduzem o direito em degraus (art. 130)."
        />
      )}

      <Cartao titulo="Programações"
              subtitulo={programacoes.data?.length
                ? `${plural(programacoes.data.length, "programação", "programações")}`
                : "Nenhuma programada"}>
        {programacoes.isPending && <Carregando linhas={3} nu />}
        {programacoes.isSuccess && programacoes.data.length === 0 && (
          <p className="text-sm" style={{ color: "var(--faint-foreground)" }}>
            Sem férias programadas. O aviso precisa sair 30 dias antes do início.
          </p>
        )}
        {programacoes.isSuccess && programacoes.data.length > 0 && (
          <ul className="eop-ferias">
            {programacoes.data.map((p) => {
              const proximo = PROXIMO[p.estado];
              // O aviso tem que sair 30 dias antes (art. 135). Menos que isso
              // com o aviso ainda não entregue é descumprimento, e a tela
              // precisa dizer antes, não depois.
              const avisoTarde = !p.avisadoEm && p.diasAteInicio < 30 && p.diasAteInicio >= 0;
              return (
                <li key={p.id} data-atencao={avisoTarde || undefined}>
                  <Palmtree aria-hidden size={15} className="eop-ferias-icone" />
                  <span className="min-w-0 flex-1">
                    <span className="eop-ferias-nome">{p.nome}</span>
                    <span className="eop-ferias-sub">
                      {fData(p.inicio)} a {fData(p.fim)} · {plural(p.dias, "dia", "dias")}
                      {p.diasAbono > 0 && ` + ${p.diasAbono}d de abono`}
                      {avisoTarde && " · aviso fora do prazo de 30 dias"}
                    </span>
                  </span>
                  <span className="pilula">{ESTADO[p.estado] ?? p.estado}</span>
                  {podeAprovar && proximo && (
                    <button className="botao h-7 px-2 text-xs"
                            disabled={avancar.isPending}
                            onClick={() => avancar.mutate({ id: p.id, estado: proximo.estado })}>
                      <Check aria-hidden size={13} /> {proximo.rotulo}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Cartao>

      {programando && (
        <FormFerias periodos={periodos.data ?? []}
                    aoFechar={() => setProgramando(false)}
                    aoCriar={() => { setProgramando(false);
                      void qc.invalidateQueries({ queryKey: ["ferias"] }); }} />
      )}
    </Pagina>
  );
}

function FormFerias(
  { periodos, aoFechar, aoCriar }:
  { periodos: { id: string; vinculoId: string; nome: string; diasSaldo: number }[];
    aoFechar: () => void; aoCriar: () => void },
) {
  const [periodoId, setPeriodoId] = useState(periodos[0]?.id ?? "");
  const [inicio, setInicio] = useState("");
  const [dias, setDias] = useState(30);
  const [diasAbono, setDiasAbono] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const periodo = periodos.find((p) => p.id === periodoId);

  const criar = useMutation({
    mutationFn: () => trpc.fechamento.programarFerias.mutate({
      vinculoId: periodo!.vinculoId, periodoAquisitivoId: periodoId,
      inicio, dias, diasAbono, adiantarDecimo: false,
    }),
    onSuccess: aoCriar,
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  // Art. 134 §1º: até três períodos, um de no mínimo 14 dias e os demais de no
  // mínimo 5. O abono conta contra o direito, não é dia extra.
  const excede = periodo ? dias + diasAbono > periodo.diasSaldo : false;

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="Programar férias"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "30rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Programar férias</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            Até três períodos, um de no mínimo 14 dias e os demais de no mínimo 5.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); criar.mutate(); }}>
          <label className="block">
            <span className="eop-rotulo">Quem</span>
            <select className="campo mt-1.5" value={periodoId}
                    onChange={(e) => setPeriodoId(e.target.value)}>
              {periodos.map((p) => (
                <option key={p.id} value={p.id}>{p.nome} · saldo de {p.diasSaldo} dias</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3.5 sm:grid-cols-3">
            <label className="block sm:col-span-1">
              <span className="eop-rotulo">Início</span>
              <input className="campo mt-1.5" type="date" required value={inicio}
                     onChange={(e) => setInicio(e.target.value)} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Dias de gozo</span>
              <input className="campo mt-1.5 num" type="number" min={5} max={30} required
                     value={dias} onChange={(e) => setDias(Number(e.target.value))} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Abono</span>
              <input className="campo mt-1.5 num" type="number" min={0} max={10}
                     value={diasAbono} onChange={(e) => setDiasAbono(Number(e.target.value))} />
            </label>
          </div>
          {excede && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} />
              <span>Gozo mais abono passam do saldo de {periodo!.diasSaldo} dias.</span>
            </p>
          )}
          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={criar.isPending || excede || !inicio}>
              {criar.isPending ? "Salvando" : "Programar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
